import {
  Button,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  FileButton,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Bell, ImagePlus, Send } from "lucide-react";
import React from "react";
import useValidate from "../hooks/useValidate";
import { addUserNotification } from "../utils/api";
import { useMovies, useSeries } from "../utils/queries";
import useStore from "../utils/store";

const Notification = () => {
  const [loading, setLoading] = React.useState(false);
  const [notification, setNotification] = React.useState({});
  const [selectedType, setSelectedType] = React.useState(null);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [imageFile, setImageFile] = React.useState(null);
  const user = useStore((state) => state.user);

  const { data: movies } = useMovies(1, 1000, "Approved");
  const { data: series } = useSeries(1, 1000, "Approved");

  const notificationTypes = [
    { value: "Movie", label: "Movie" },
    { value: "Series", label: "Series" },
    { value: "Subscription", label: "Subscription" },
  ];

  const addUserNotifications = async () => {
    if (
      !notification?.title ||
      !notification?.description ||
      !imageFile ||
      !selectedType
    )
      return notifications.show({
        message: "Please fill all the fields",
        color: "red",
      });

    setLoading(true);
    const formData = new FormData();
    formData.append("title", notification.title);
    formData.append("description", notification.description);
    formData.append("image", imageFile);
    formData.append("type", selectedType);

    if (
      (selectedType === "Movie" || selectedType === "Series") &&
      selectedItem
    ) {
      formData.append("id", selectedItem);
    }

    const [success] = await addUserNotification(formData);
    if (success) {
      notifications.show({
        message: "Notification added successfully",
        color: "green",
      });
      setNotification({});
      setSelectedType(null);
      setSelectedItem(null);
      setImageFile(null);
    }
    setLoading(false);
  };

  useValidate();

  if (!user?.userNotifications && !user?.addNotification) {
    return (
      <div className="form-page">
        <Title order={2} className="form-page-title">
          Push Notifications
        </Title>
        <Paper className="form-card" radius="md" p="xl">
          <div className="flex items-center gap-3 text-red-400">
            <Bell size={24} />
            <Text size="lg" fw={600}>
              You don&apos;t have permission to view this page
            </Text>
          </div>
        </Paper>
      </div>
    );
  }

  return (
    <div className="form-page">
      <Title order={2} className="form-page-title">
        Push Notifications
      </Title>
      <Text c="dimmed" size="sm" mb="lg">
        Send push notifications to app users for movies, series, or subscription updates.
      </Text>

      <Paper className="form-card" radius="md" p="xl">
        <Stack gap="lg">
          <Select
            label="Notification Type"
            placeholder="Select notification type"
            data={notificationTypes}
            value={selectedType}
            onChange={(value) => {
              setSelectedType(value);
              setSelectedItem(null);
            }}
          />

          {(selectedType === "Movie" || selectedType === "Series") && (
            <Select
              label={`Select ${selectedType}`}
              placeholder={`Choose a ${selectedType.toLowerCase()}`}
              data={
                selectedType === "Movie"
                  ? (movies?.data || []).map((m) => ({
                      value: m._id,
                      label: m.name,
                    }))
                  : (series?.data || []).map((s) => ({
                      value: s._id,
                      label: s.name,
                    }))
              }
              value={selectedItem}
              onChange={setSelectedItem}
              searchable
            />
          )}

          <TextInput
            label="Title"
            placeholder="Enter notification title"
            value={notification.title || ""}
            onChange={(e) =>
              setNotification({ ...notification, title: e.target.value })
            }
          />

          <Textarea
            label="Description"
            placeholder="Enter notification description"
            value={notification.description || ""}
            onChange={(e) =>
              setNotification({
                ...notification,
                description: e.target.value,
              })
            }
            minRows={3}
          />

          <div>
            <Text size="sm" fw={500} mb={6} c="dimmed">
              Image
            </Text>
            <div className="flex items-center gap-3 flex-wrap">
              <FileButton accept="image/*" onChange={setImageFile}>
                {(props) => (
                  <Button
                    {...props}
                    variant="light"
                    leftSection={<ImagePlus size={18} />}
                  >
                    {imageFile ? "Change Image" : "Upload Image"}
                  </Button>
                )}
              </FileButton>
              {imageFile && (
                <>
                  <Text size="sm" c="dimmed" className="truncate max-w-[200px]">
                    {imageFile.name}
                  </Text>
                  <Button
                    variant="subtle"
                    size="xs"
                    color="red"
                    onClick={() => setImageFile(null)}
                  >
                    Remove
                  </Button>
                </>
              )}
            </div>
          </div>

          {user?.addNotification && (
            <Button
              onClick={addUserNotifications}
              loading={loading}
              leftSection={<Send size={18} />}
              size="md"
              mt="md"
            >
              Send Notification
            </Button>
          )}
        </Stack>
      </Paper>
    </div>
  );
};

export default Notification;
